import {
  Persona_Inquiry_Status_Enum,
  useAcceptUserAgreementMutation,
  useGetAgreementsByUserIdQuery,
  useGetChildVerificationStatusByEmailSubscription,
  useGetChildVerificationStatusQuery,
  useGetSubPatientsByParentQuery,
  UserAgreementTypes,
} from 'graphql/generated/hasura';
import { useAuth } from 'auth/context/AuthContext';
import { useMemo } from 'react';
import useProfileDataLoader from 'hooks/usePatientDataLoader';
import { Roles } from '../../../../firebase/interfaces';
import moment from 'moment';

const today = new Date();
const MIN_AGE = 13;
export const dateStartNeedAgreement = new Date(
  today.getFullYear() - MIN_AGE,
  today.getMonth(),
  today.getDate(),
);

export const useVerifyChild = () => {
  const { user: loggedUser, selectedAccount } = useAuth();

  const { data: subPatients, loading: dobLoading } =
    useGetSubPatientsByParentQuery({
      skip: loggedUser?.role !== Roles.PATIENT,
    });

  const dob = useMemo(
    () =>
      (subPatients?.getSubPatientsByParent?.subPatients ?? []).find(
        ({ id }) => id === selectedAccount,
      )?.birthDate,
    [selectedAccount, subPatients],
  );

  const { data: userVerificationSocketData } =
    useGetChildVerificationStatusByEmailSubscription({
      fetchPolicy: 'network-only',
      variables: {
        id: selectedAccount || loggedUser?.uuid || '',
      },
    });

  const { data: userVerificationData, loading } =
    useGetChildVerificationStatusQuery({
      fetchPolicy: 'network-only',
      skip: loggedUser?.role !== Roles.PATIENT,
      variables: {
        id: selectedAccount || loggedUser?.uuid || '',
      },
    });

  const [acceptUserAgreement, { loading: submitting }] =
    useAcceptUserAgreementMutation({
      variables: { agreementType: UserAgreementTypes.CoppaChild },
      refetchQueries: ['GetAgreementsByUserId'],
    });

  const { data: userAgreements, loading: loadingAgreement } =
    useGetAgreementsByUserIdQuery({
      fetchPolicy: 'network-only',
      skip: loggedUser?.role !== Roles.PATIENT,
      variables: { id: selectedAccount || loggedUser?.uuid || '' },
    });

  const { fhirUserData: profileData } = useProfileDataLoader();
  const data = useMemo(() => {
    return {
      sudAgreement:
        (userAgreements?.user_agreements ?? [])?.find(
          (ag) =>
            ag.agreement_type === UserAgreementTypes.NoticeOfPrivacySudRecords,
        )?.status ?? null,
      agreement: moment(dob).isAfter(dateStartNeedAgreement)
        ? userAgreements?.user_agreements?.find(
            (ag) => ag.agreement_type === UserAgreementTypes.CoppaChild,
          )?.status ?? null
        : Persona_Inquiry_Status_Enum.Approved,
      kyc_child_status:
        userVerificationSocketData?.users[0]?.kyc_child_status ||
        userVerificationData?.users[0]?.kyc_child_status ||
        null,
    };
  }, [
    dob,
    userAgreements?.user_agreements,
    userVerificationData?.users,
    userVerificationSocketData?.users,
  ]);

  const isVerify = useMemo(() => {
    const { agreement, kyc_child_status, sudAgreement } = data;
    return (
      agreement === Persona_Inquiry_Status_Enum.Approved &&
      sudAgreement === Persona_Inquiry_Status_Enum.Approved &&
      (kyc_child_status === Persona_Inquiry_Status_Enum.Approved ||
        kyc_child_status === Persona_Inquiry_Status_Enum.Completed)
    );
  }, [data]);

  return {
    acceptUserAgreement,
    dob,
    loading: loading || submitting || loadingAgreement || dobLoading,
    data,
    isVerify,
    profileData,
  };
};
